import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  FluentProvider,
  Input,
  Popover,
  PopoverSurface,
  Spinner,
  Text,
  makeStyles,
  tokens,
  type Theme,
} from '@fluentui/react-components';
import { useActiveTheme } from '../theme/ThemeContext';
import {
  Briefcase20Regular,
  ClipboardTask20Regular,
  Cube20Regular,
  Dismiss20Regular,
  Search20Regular,
} from '@fluentui/react-icons';
import type { IGlobalSearchResponse } from '@moko/utils';

type GlobalSearchScope = 'all' | 'works' | 'orders' | 'assets';
type GlobalSearchItemType = Exclude<GlobalSearchScope, 'all'>;

export interface IGlobalSearchItem {
  id: number;
  type: GlobalSearchItemType;
  title: string;
}

interface IGlobalSearchGroup {
  key: GlobalSearchItemType;
  label: string;
  singularLabel: string;
  items: IGlobalSearchItem[];
}

export interface GlobalSearchBarProps {
  searchFn: (
    abortController: AbortController,
    query: string
  ) => Promise<IGlobalSearchResponse>;
  onResultClick: (item: IGlobalSearchItem) => void;
  placeholder?: string;
  mobileBreakpoint?: number;
  theme?: Theme;
}

const MIN_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 250;

const emptyResponse: IGlobalSearchResponse = {
  works: [],
  orders: [],
  assets: [],
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: 0,
    width: '100%',
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    maxWidth: '440px',
    width: '100%',
  },
  input: {
    width: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow2,
  },
  iconButton: {
    minWidth: '36px',
    width: '36px',
    height: '36px',
  },
  popover: {
    padding: '10px 0',
    width: 'min(460px, calc(100vw - 32px))',
    maxHeight: '70vh',
    overflowY: 'auto',
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: '0 8px 16px rgba(0,0,0,0.14), 0 0 2px rgba(0,0,0,0.12)',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  filterRow: {
    display: 'flex',
    gap: '8px',
    padding: '0 12px 10px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexWrap: 'wrap',
  },
  filterButton: {
    borderRadius: '9999px !important',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 10px',
    minHeight: '30px',
  },
  filterLabel: {
    lineHeight: '1',
  },
  filterBadge: {
    minWidth: '22px',
    height: '22px',
    fontSize: '12px',
    lineHeight: '22px',
    padding: '0 6px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    paddingTop: '8px',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px 2px 16px',
  },
  groupTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  resultsCount: {
    color: tokens.colorNeutralForeground3,
  },
  resultButton: {
    width: '100%',
    justifyContent: 'flex-start',
    textAlign: 'left',
    padding: '10px 16px',
    borderRadius: '0',
    color: tokens.colorNeutralForeground1,
  },
  resultInner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    width: '100%',
    minWidth: 0,
  },
  resultBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    flex: 1,
  },
  resultTitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: tokens.fontWeightMedium,
  },
  resultMeta: {
    color: tokens.colorNeutralForeground3,
  },
  state: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '16px',
    color: tokens.colorNeutralForeground3,
  },
});

const normalizeGroups = (
  response: IGlobalSearchResponse,
  t: (key: string, fallback: string) => string
): IGlobalSearchGroup[] => [
  {
    key: 'works',
    label: t('works', 'Works'),
    singularLabel: t('work', 'Work'),
    items: response.works.map((work) => ({
      id: work.id,
      type: 'works' as const,
      title: work.description,
    })),
  },
  {
    key: 'orders',
    label: t('orders', 'Orders'),
    singularLabel: t('order', 'Order'),
    items: response.orders.map((order) => ({
      id: order.id,
      type: 'orders' as const,
      title: order.description,
    })),
  },
  {
    key: 'assets',
    label: t('assets', 'Assets'),
    singularLabel: t('asset', 'Asset'),
    items: response.assets.map((asset) => ({
      id: asset.id,
      type: 'assets' as const,
      title: asset.name,
    })),
  },
];

const getResultIcon = (type: GlobalSearchItemType) => {
  if (type === 'works') return <Briefcase20Regular />;
  if (type === 'orders') return <ClipboardTask20Regular />;
  return <Cube20Regular />;
};

const getViewportMatch = (breakpoint: number) => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= breakpoint;
};

const GlobalSearchBar = ({
  searchFn,
  onResultClick,
  placeholder,
  mobileBreakpoint = 768,
  theme: themeProp,
}: GlobalSearchBarProps) => {
  const { t } = useTranslation();
  const activeTheme = useActiveTheme();
  const theme = themeProp ?? activeTheme;
  const styles = useStyles();
  const placeholderText =
    placeholder ?? t('globalSearchPlaceholder', 'Search works, orders, assets');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const mouseDownInsideRef = useRef(false);
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<GlobalSearchScope>('all');
  const [response, setResponse] =
    useState<IGlobalSearchResponse>(emptyResponse);
  const [isLoading, setIsLoading] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isCompact, setIsCompact] = useState(() =>
    getViewportMatch(mobileBreakpoint)
  );
  const [isCompactExpanded, setIsCompactExpanded] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const compact = getViewportMatch(mobileBreakpoint);
      setIsCompact(compact);
      if (!compact) setIsCompactExpanded(false);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBreakpoint]);

  useEffect(() => {
    if (isCompact && isCompactExpanded) {
      inputRef.current?.focus();
    }
  }, [isCompact, isCompactExpanded]);

  useEffect(() => {
    const trimmedQuery = query.trim();

    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      setResponse(emptyResponse);
      setIsLoading(false);
      setHasSearched(false);
      return;
    }

    const abortController = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        setIsLoading(true);
        setHasSearched(false);
        const result = await searchFn(abortController, trimmedQuery);
        setResponse(result ?? emptyResponse);
        setIsPopoverOpen(true);
        setHasSearched(true);
      } catch {
        setResponse(emptyResponse);
      } finally {
        setIsLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [query, searchFn]);

  const groups = useMemo(() => normalizeGroups(response, t), [response, t]);

  const visibleGroups = useMemo(() => {
    if (scope === 'all') return groups.filter((g) => g.items.length > 0);
    return groups.filter((g) => g.key === scope);
  }, [groups, scope]);

  const totalResults = useMemo(
    () => groups.reduce((t, g) => t + g.items.length, 0),
    [groups]
  );

  const scopeOptions = useMemo(
    () => [
      { key: 'all' as const, label: t('all', 'All'), count: totalResults },
      {
        key: 'works' as const,
        label: t('works', 'Works'),
        count: response.works.length,
      },
      {
        key: 'orders' as const,
        label: t('orders', 'Orders'),
        count: response.orders.length,
      },
      {
        key: 'assets' as const,
        label: t('assets', 'Assets'),
        count: response.assets.length,
      },
    ],
    [
      t,
      response.assets.length,
      response.orders.length,
      response.works.length,
      totalResults,
    ]
  );

  const shouldShowInput = !isCompact || isCompactExpanded;
  const shouldOpenPopover =
    shouldShowInput && isPopoverOpen && query.trim().length >= MIN_QUERY_LENGTH;

  const handleResultClick = useCallback(
    (item: IGlobalSearchItem) => {
      setIsPopoverOpen(false);
      if (isCompact) setIsCompactExpanded(false);
      onResultClick(item);
    },
    [isCompact, onResultClick]
  );

  const handlePopoverOpenChange = useCallback(
    (_event: unknown, data: { open: boolean }) => {
      if (!data.open && mouseDownInsideRef.current) return;
      setIsPopoverOpen(data.open);
      if (!data.open && isCompact && !query.trim()) {
        setIsCompactExpanded(false);
      }
    },
    [isCompact, query]
  );

  const searchInput = shouldShowInput ? (
    <>
      <Input
        ref={inputRef}
        type="search"
        value={query}
        placeholder={placeholderText}
        appearance="outline"
        className={styles.input}
        contentAfter={<Search20Regular />}
        onFocus={() => {
          if (query.trim().length >= MIN_QUERY_LENGTH || isLoading) {
            setIsPopoverOpen(true);
          }
        }}
        onChange={(_event, data) => {
          setQuery(data.value);
          if (data.value.trim().length >= MIN_QUERY_LENGTH) {
            setIsPopoverOpen(true);
          } else {
            setIsPopoverOpen(false);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setIsPopoverOpen(false);
            if (isCompact && !query.trim()) {
              setIsCompactExpanded(false);
            }
          }
        }}
      />
      {isCompact && (
        <Button
          appearance="subtle"
          icon={<Dismiss20Regular />}
          aria-label={t('closeSearch', 'Close search')}
          onClick={() => {
            setIsPopoverOpen(false);
            setIsCompactExpanded(false);
            setQuery('');
          }}
          style={{ flexShrink: 0 }}
        />
      )}
    </>
  ) : (
    <Button
      appearance="subtle"
      icon={<Search20Regular />}
      aria-label={t('openGlobalSearch', 'Open global search')}
      className={styles.iconButton}
      onClick={() => {
        setIsCompactExpanded(true);
        setIsPopoverOpen(query.trim().length >= MIN_QUERY_LENGTH);
      }}
    />
  );

  const rootStyle: React.CSSProperties | undefined =
    isCompact && isCompactExpanded
      ? {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: tokens.colorNeutralBackground1,
          zIndex: 10,
          padding: '0 8px',
          justifyContent: 'stretch',
        }
      : undefined;

  const triggerStyle: React.CSSProperties | undefined =
    isCompact && isCompactExpanded ? { maxWidth: 'none', gap: 8 } : undefined;

  return (
    <div className={styles.root} style={rootStyle}>
      <div
        ref={triggerRef}
        className={styles.trigger}
        style={triggerStyle}
        onMouseDown={() => {
          mouseDownInsideRef.current = true;
        }}
        onMouseUp={() => {
          window.setTimeout(() => {
            mouseDownInsideRef.current = false;
          }, 0);
        }}
      >
        {searchInput}
      </div>
      <Popover
        open={shouldOpenPopover}
        onOpenChange={handlePopoverOpenChange}
        trapFocus={false}
        unstable_disableAutoFocus
        positioning={{
          position: 'below',
          align: 'start',
          target: triggerRef.current,
        }}
      >
        <PopoverSurface className={styles.popover}>
          <FluentProvider theme={theme} style={{ background: 'transparent' }}>
            <div className={styles.filterRow}>
              {scopeOptions.map((option) => (
                <Button
                  key={option.key}
                  size="small"
                  shape="rounded"
                  appearance={scope === option.key ? 'primary' : 'subtle'}
                  className={styles.filterButton}
                  onClick={() => setScope(option.key)}
                >
                  <span className={styles.filterLabel}>{option.label}</span>
                  <Badge
                    appearance="filled"
                    size="small"
                    className={styles.filterBadge}
                  >
                    {option.count}
                  </Badge>
                </Button>
              ))}
            </div>
            <div className={styles.content}>
              {isLoading && (
                <div className={styles.state}>
                  <Spinner size="tiny" />
                  <Text>{t('searching', 'Searching...')}</Text>
                </div>
              )}
              {!isLoading && query.trim().length < MIN_QUERY_LENGTH && (
                <div className={styles.state}>
                  <Text>
                    {t(
                      'searchMinCharacters',
                      'Type at least {{count}} characters',
                      { count: MIN_QUERY_LENGTH }
                    )}
                  </Text>
                </div>
              )}
              {!isLoading &&
                query.trim().length >= MIN_QUERY_LENGTH &&
                hasSearched &&
                totalResults === 0 && (
                  <div className={styles.state}>
                    <Text>{t('noResultsFound', 'No results found')}</Text>
                  </div>
                )}
              {!isLoading &&
                visibleGroups.map((group) => (
                  <div key={group.key} className={styles.group}>
                    <div className={styles.groupHeader}>
                      <Text className={styles.groupTitle}>{group.label}</Text>
                      <Text className={styles.resultsCount}>
                        {group.items.length}
                      </Text>
                    </div>
                    {group.items.map((item) => (
                      <Button
                        key={`${item.type}-${item.id}`}
                        appearance="subtle"
                        className={styles.resultButton}
                        onClick={() => handleResultClick(item)}
                      >
                        <div className={styles.resultInner}>
                          {getResultIcon(item.type)}
                          <div className={styles.resultBody}>
                            <Text className={styles.resultTitle}>
                              {item.title}
                            </Text>
                            <Text
                              className={styles.resultMeta}
                            >{`${group.singularLabel} #${item.id}`}</Text>
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                ))}
            </div>
          </FluentProvider>
        </PopoverSurface>
      </Popover>
    </div>
  );
};

export default GlobalSearchBar;
